import { useEffect, useMemo, useState } from 'react';
import { Plus, Search, Sparkles } from 'lucide-react';
import type { PromocionVista, PromocionCrearInput, ProductoVista, PaqueteVista } from '@shared/types';
import { FormPromocion } from '@renderer/components/forms/FormPromocion';
import { useNegocioStore } from '@renderer/store/useNegocioStore';
import { PromocionCard } from '@renderer/components/promociones/PromocionCard';
import { estadoPromocion, type EstadoPromo } from '@renderer/components/promociones/promocionHelpers';
import { HelpButton } from '@renderer/components/help/HelpButton';
import { tourPromociones } from '@renderer/tours';

/**
 * Sprint 16 — Promociones rediseñadas a grid de cards con lenguaje natural.
 *
 * Antes: tabla 8 columnas con jerga técnica ("DESCUENTO_PORCENTAJE",
 *        "Pri.", "Elegibles: 2 productos") — la dueña no parseaba.
 * Después: cards con título traducido ("20% de descuento"), estado real
 *          (activa AHORA vs fuera de horario), toggle pausar/reanudar
 *          inline.
 */

type FiltroEstado = 'todos' | 'activas' | 'programadas' | 'pausadas' | 'vencidas';

export function PromosPage() {
  const [promos, setPromos] = useState<PromocionVista[]>([]);
  const [productos, setProductos] = useState<ProductoVista[]>([]);
  const [paquetes, setPaquetes] = useState<PaqueteVista[]>([]);
  const [editando, setEditando] = useState<PromocionVista | null>(null);
  const [drawerAbierto, setDrawerAbierto] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [filtro, setFiltro] = useState<FiltroEstado>('todos');
  const negocio = useNegocioStore((s) => s.negocio);
  const moneda = negocio?.monedaSimbolo ?? '$';
  const locale = negocio?.locale ?? 'es-MX';

  const cargar = async () => {
    setCargando(true);
    const [promRes, prodRes, paqRes] = await Promise.all([
      window.api.promociones.listar(),
      window.api.productos.listar(),
      window.api.paquetes.listar(),
    ]);
    if (promRes.ok) setPromos(promRes.data);
    if (prodRes.ok) setProductos(prodRes.data);
    if (paqRes.ok) setPaquetes(paqRes.data);
    setCargando(false);
  };

  useEffect(() => {
    cargar();
  }, []);

  const onEliminar = async (p: PromocionVista) => {
    const ok = await window.confirmar({
      titulo: 'Eliminar promoción',
      mensaje: `¿Eliminar la promoción "${p.nombre}"?`,
      textoConfirmar: 'Eliminar',
      variant: 'destructive',
    });
    if (!ok) return;
    const res = await window.api.promociones.eliminar(p.id);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
  };

  // Toggle activa/inactiva sin abrir formulario. Reconstruye el DTO desde la vista.
  const onToggleActiva = async (p: PromocionVista) => {
    const data: PromocionCrearInput = {
      nombre: p.nombre,
      descripcion: p.descripcion ?? undefined,
      tipo: p.tipo,
      valor: p.valor ?? undefined,
      topeMonto: p.topeMonto ?? undefined,
      productoRegaloId: p.productoRegaloId ?? undefined,
      cantidadRegalo: p.cantidadRegalo ?? undefined,
      cantidadMinima: p.cantidadMinima,
      vigenciaInicio: p.vigenciaInicio,
      vigenciaFin: p.vigenciaFin,
      diasSemana: p.diasSemana ?? undefined,
      acumulable: p.acumulable,
      activa: !p.activa,
      prioridad: p.prioridad,
      productosElegiblesIds: p.productosElegiblesIds,
      paquetesElegiblesIds: p.paquetesElegiblesIds,
    };
    const res = await window.api.promociones.actualizar(p.id, data);
    if (res.ok) cargar();
    else window.alertar({ titulo: 'Error', mensaje: res.error, variant: 'warn' });
  };

  // Agrupar por estado para los contadores de los chips.
  const counts = useMemo(() => {
    const c = { todos: promos.length, activas: 0, programadas: 0, pausadas: 0, vencidas: 0 };
    for (const p of promos) {
      const est = estadoPromocion(p);
      if (est === 'pausada') c.pausadas++;
      else if (est === 'vencida') c.vencidas++;
      else if (est === 'programada') c.programadas++;
      else c.activas++; // activa_hoy | vence_pronto | fuera_horario
    }
    return c;
  }, [promos]);

  const matchFiltro = (est: EstadoPromo, f: FiltroEstado): boolean => {
    switch (f) {
      case 'todos': return true;
      case 'activas': return est === 'activa_hoy' || est === 'vence_pronto' || est === 'fuera_horario';
      case 'programadas': return est === 'programada';
      case 'pausadas': return est === 'pausada';
      case 'vencidas': return est === 'vencida';
    }
  };

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return promos
      .filter((p) => (q ? p.nombre.toLowerCase().includes(q) : true))
      .filter((p) => matchFiltro(estadoPromocion(p), filtro))
      .sort((a, b) => {
        // Orden visual: activas hoy > vence pronto > fuera horario > programadas > pausadas > vencidas.
        const orden: Record<EstadoPromo, number> = {
          activa_hoy: 0, vence_pronto: 1, fuera_horario: 2,
          programada: 3, pausada: 4, vencida: 5,
        };
        const ea = orden[estadoPromocion(a)];
        const eb = orden[estadoPromocion(b)];
        if (ea !== eb) return ea - eb;
        return a.nombre.localeCompare(b.nombre);
      });
  }, [promos, busqueda, filtro]);

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-3" data-tour="prom-header">
        <div className="flex items-center gap-3">
          <Sparkles className="h-7 w-7 text-primary" />
          <div>
            <h1 className="text-2xl font-bold">Promociones</h1>
            <p className="text-sm text-slate-500">
              Descuentos, regalos y precios fijos. Se aplican automáticamente al cobrar.
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => {
              setEditando(null);
              setDrawerAbierto(true);
            }}
            className="btn-primary"
            data-tour="prom-nueva"
          >
            <Plus className="h-4 w-4" />
            Nueva promoción
          </button>
          <HelpButton tour={tourPromociones} />
        </div>
      </header>

      {promos.length === 0 && !cargando ? (
        <div className="card text-center py-8 text-slate-500">
          <Sparkles className="h-12 w-12 mx-auto mb-3 text-slate-300" />
          <p className="font-medium">Aún no tienes promociones configuradas</p>
          <p className="text-sm mb-4">Crea descuentos, regalos o precios fijos para atraer clientes.</p>
          <button
            onClick={() => {
              setEditando(null);
              setDrawerAbierto(true);
            }}
            className="btn-primary"
          >
            <Plus className="h-4 w-4" /> Crear primera promoción
          </button>
        </div>
      ) : (
        <>
          {/* Filtros + búsqueda */}
          <div className="card flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-tour="prom-filtros">
            <div className="flex flex-wrap gap-2">
              <Chip etiqueta="Todas"      count={counts.todos}       activo={filtro === 'todos'}       color="slate"  onClick={() => setFiltro('todos')} />
              <Chip etiqueta="🟢 Activas" count={counts.activas}     activo={filtro === 'activas'}     color="emerald" onClick={() => setFiltro('activas')} />
              <Chip etiqueta="📅 Programadas" count={counts.programadas} activo={filtro === 'programadas'} color="sky"    onClick={() => setFiltro('programadas')} />
              <Chip etiqueta="⏸ Pausadas"  count={counts.pausadas}    activo={filtro === 'pausadas'}    color="slate"  onClick={() => setFiltro('pausadas')} />
              <Chip etiqueta="🔴 Vencidas" count={counts.vencidas}    activo={filtro === 'vencidas'}    color="red"    onClick={() => setFiltro('vencidas')} />
            </div>
            <div className="relative md:w-72">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <input
                type="text"
                className="input pl-9"
                placeholder="Buscar promoción…"
                value={busqueda}
                onChange={(e) => setBusqueda(e.target.value)}
              />
            </div>
          </div>

          {/* Grid de cards */}
          {cargando ? (
            <div className="card text-center py-8 text-slate-400">Cargando…</div>
          ) : filtradas.length === 0 ? (
            <div className="card text-center py-8 text-slate-500">
              No hay promociones que coincidan con el filtro.
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-tour="prom-cards">
              {filtradas.map((p) => (
                <PromocionCard
                  key={p.id}
                  promo={p}
                  productos={productos}
                  paquetes={paquetes}
                  moneda={moneda}
                  locale={locale}
                  onEditar={() => {
                    setEditando(p);
                    setDrawerAbierto(true);
                  }}
                  onEliminar={() => onEliminar(p)}
                  onToggleActiva={() => onToggleActiva(p)}
                />
              ))}
            </div>
          )}
        </>
      )}

      <FormPromocion
        abierto={drawerAbierto}
        promocion={editando}
        productos={productos}
        paquetes={paquetes}
        onClose={() => setDrawerAbierto(false)}
        onSaved={() => {
          setDrawerAbierto(false);
          cargar();
        }}
      />
    </div>
  );
}

function Chip({
  etiqueta, count, activo, color, onClick,
}: {
  etiqueta: string;
  count: number;
  activo: boolean;
  color: 'slate' | 'emerald' | 'sky' | 'red';
  onClick: () => void;
}) {
  const colorMap = {
    slate:   'bg-slate-700 text-white border-slate-700',
    emerald: 'bg-emerald-600 text-white border-emerald-600',
    sky:     'bg-sky-600 text-white border-sky-600',
    red:     'bg-red-600 text-white border-red-600',
  };
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full px-3 py-1.5 text-sm font-medium border-2 transition ${
        activo ? colorMap[color] : 'bg-white text-slate-700 border-slate-200 hover:border-slate-300'
      }`}
    >
      {etiqueta} <span className={`ml-1 text-xs ${activo ? 'opacity-90' : 'opacity-60'}`}>· {count}</span>
    </button>
  );
}
